import clsx from "clsx";
import { FC, Suspense, useEffect, useMemo } from "react";
import {
  Decimal,
  Fiat,
  floatToSubscript,
  i18n,
  useWindowSize,
} from "rujira.ui";

import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";
import { usePreloadedAccountData } from "../../services/accountData";

import { priceFormatter } from "rujira.js";
import { TradeBreakpoint } from "../Trade";
import { useTradeBookSubscription } from "../TradeSubscriptions";
import { adjust, usePrice } from "./Context";
import { OrderBookAccountFragment$key } from "./__generated__/OrderBookAccountFragment.graphql";
import { OrderBookEntryFragment$key } from "./__generated__/OrderBookEntryFragment.graphql";
import {
  OrderBookFragment$data,
  OrderBookFragment$key,
} from "./__generated__/OrderBookFragment.graphql";
import { OrderBookSubFragment$key } from "./__generated__/OrderBookSubFragment.graphql";

export enum BookFilterOption {
  All = "all",
  Buy = "buy",
  Sell = "sell",
}
const fragment = graphql`
  fragment OrderBookFragment on FinBook {
    id
    pair {
      address
      assetBase {
        asset
        metadata {
          symbol
          decimals
        }
      }
      assetQuote {
        asset
        metadata {
          symbol
          decimals
        }
        price {
          current
        }
      }
      tick
    }
    ...OrderBookSubFragment
  }
`;

const subFrag = graphql`
  fragment OrderBookSubFragment on FinBook {
    id
    center
    spread
    bids {
      value
      price
      total
      total
      virtualTotal
      virtualValue
      ...OrderBookEntryFragment
    }
    asks {
      value
      price
      total
      total
      virtualTotal
      virtualValue
      ...OrderBookEntryFragment
    }
  }
`;

const accountFragment = graphql`
  fragment OrderBookAccountFragment on Account {
    fin {
      orders(first: 100) {
        edges {
          node {
            pair {
              address
            }
            rate
            remaining
          }
        }
      }
    }
  }
`;

export const BookFilter: FC<{
  setFilter: (f: BookFilterOption) => void;
  isSkeleton?: boolean;
}> = ({ setFilter }) => {
  return (
    <div className="trade__book-switch mb-2">
      {/* <nav className="tabs tabs--sm">
        <i18n.a className="selected">Order Book</i18n.a>
      </nav> */}
      <i18n.h3 className="fs-12 fw-500 ml-1 mr-1 color-white">
        Order Book
      </i18n.h3>

      <div className="trade__book-filter ml-1">
        <svg
          data-tooltip-id="book-tip"
          data-tooltip-html="Split depth"
          onClick={() => setFilter(BookFilterOption.All)}
          className="pointer"
          width="16px"
          height="16px"
          viewBox="0 0 16 16"
          xmlns="http://www.w3.org/2000/svg">
          <path
            d="M2,0 L14,0 C15.1045695,-2.02906125e-16 16,0.8954305 16,2 L16,8 L16,8 L0,8 L0,2 C-1.3527075e-16,0.8954305 0.8954305,2.02906125e-16 2,0 Z"
            fill="#E53935"></path>
          <path
            d="M0,8 L16,8 L16,14 C16,15.1045695 15.1045695,16 14,16 L2,16 C0.8954305,16 1.3527075e-16,15.1045695 0,14 L0,8 L0,8 Z"
            fill="#60FBD0"></path>
        </svg>
        <svg
          data-tooltip-id="book-tip"
          data-tooltip-html="Sell depth"
          onClick={() => setFilter(BookFilterOption.Sell)}
          className="pointer"
          width="16px"
          height="16px"
          viewBox="0 0 16 16"
          xmlns="http://www.w3.org/2000/svg">
          <path
            d="M2,0 L14,0 C15.1045695,-2.02906125e-16 16,0.8954305 16,2 L16,8 L16,8 L0,8 L0,2 C-1.3527075e-16,0.8954305 0.8954305,2.02906125e-16 2,0 Z"
            fill="#E53935"></path>
          <path
            d="M0,9 L16,9 L16,14 C16,15.1045695 15.1045695,16 14,16 L2,16 C0.8954305,16 1.3527075e-16,15.1045695 0,14 L0,9 L0,9 Z"
            fillOpacity="0.5"
            fill="#607D8B"></path>
        </svg>
        <svg
          data-tooltip-id="book-tip"
          data-tooltip-html="Buy depth"
          onClick={() => setFilter(BookFilterOption.Buy)}
          className="pointer"
          width="16px"
          height="16px"
          viewBox="0 0 16 16"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg">
          <path
            d="M2,0 L14,0 C15.1045695,-2.02906125e-16 16,0.8954305 16,2 L16,7 L16,7 L0,7 L0,2 C-1.3527075e-16,0.8954305 0.8954305,2.02906125e-16 2,0 Z"
            fillOpacity="0.5"
            fill="#607D8B"></path>
          <path
            d="M0,8 L16,8 L16,14 C16,15.1045695 15.1045695,16 14,16 L2,16 C0.8954305,16 1.3527075e-16,15.1045695 0,14 L0,8 L0,8 Z"
            fill="#60FBD0"></path>
        </svg>
      </div>
      {/* !isSkeleton && (
        <div className="ml-a flex ai-c">
          <div className="trade__book-stat">
            <h4>APR</h4>
            <div>{nFormatter(BigInt(0), 2)}%</div>
          </div>
          <div className="trade__book-stat">
            <h4>Liquidity</h4>
            <div>{nFormatter(BigInt(0), 2)}</div>
          </div>
          <Button
            className="button--xs button--outline ml-2 mr-1"
            label="Provide"
          />
        </div>
      ) */}
    </div>
  );
};

export const OrderBook: FC<{
  filter: BookFilterOption;
  k?: OrderBookFragment$key;
}> = ({ filter, k }) => {
  const d = useFragment(fragment, k);
  const { width } = useWindowSize();
  useTradeBookSubscription(d?.id);
  if (!k) return null;
  return (
    <>
      <div className="trade__book-header">
        <div>
          {width >= TradeBreakpoint ? "Price" : "Total"} (
          {d?.pair.assetQuote.metadata.symbol})
        </div>
        <div
          className={clsx({
            "text-right": width >= TradeBreakpoint,
            "text-center": width < TradeBreakpoint,
          })}>
          Amount ({d?.pair.assetBase.metadata.symbol})
        </div>
        <div className="text-right">
          Value ({d?.pair.assetQuote.metadata.symbol})
        </div>
      </div>
      <div className="trade__book-overflow">
        {width < TradeBreakpoint ? (
          <OrdersAll d={d || undefined} k={d || undefined} />
        ) : (
          <>
            {filter === BookFilterOption.All && (
              <OrdersAll d={d || undefined} k={d || undefined} />
            )}
            {filter === BookFilterOption.Sell && (
              <Asks d={d || undefined} k={d || undefined} />
            )}
            {filter === BookFilterOption.Buy && (
              <Bids d={d || undefined} k={d || undefined} />
            )}
          </>
        )}
      </div>
    </>
  );
};

const center = () => {
  const mid = document.querySelector<HTMLElement>(".trade__book-mid");
  const list = document.querySelector<HTMLElement>(".trade__book-overflow");
  const sell = document.querySelector<HTMLElement>(".trade__book-sells");
  if (mid && list && sell) {
    list.scrollTop =
      sell.offsetHeight + mid.offsetHeight * 0.5 - list.offsetHeight * 0.5;
  }
};

const selectMax = (a: bigint, v: { total: bigint; virtualTotal: bigint }) => {
  const x = BigInt(v.total) + BigInt(v.virtualTotal);
  return a > x ? a : x;
};

const OrdersAll: FC<{
  d?: OrderBookFragment$data;
  k?: OrderBookSubFragment$key;
}> = ({ d, k }) => {
  const { width } = useWindowSize();
  const book = useFragment(subFrag, k);

  useEffect(() => {
    center();
    setTimeout(center, 100);
  }, [book]);

  const mid = book?.center ? book.center : null;
  const spread = book?.spread ? book.spread : null;
  const midPrice = mid ? mid * (d?.pair.assetQuote.price?.current || 0n) : null;

  const [, setPrice] = usePrice();

  const [maxAsk, maxBid] = useMemo(
    () =>
      book
        ? [
            [...book.asks].reduce(selectMax, 0n),
            [...book.bids].reduce(selectMax, 0n),
          ]
        : [0n, 0n],
    [book]
  );

  return (
    <>
      <div className="trade__book-sells">
        {d && book
          ? [...book.asks]
              .slice(0, width > TradeBreakpoint ? 40 : 12)
              .map((e, i, all) => (
                <ItemAsk
                  address={d.pair.address}
                  tick={Number(d.pair.tick)}
                  max={maxAsk}
                  key={e.price}
                  k={e}
                  cumulative={all.slice(0, i + 1)}
                  symbols={[
                    d.pair.assetBase.metadata.symbol,
                    d.pair.assetQuote.metadata.symbol,
                  ]}
                />
              ))
          : null}
      </div>
      {width > TradeBreakpoint && (
        <div
          className="trade__book-mid"
          onClick={() => {
            mid && setPrice(adjust(mid, Number(d?.pair.tick)));
          }}>
          <div className="flex wrap ai-c">
            <h3>
              {priceFormatter(mid || 0n, {
                precision: Number(d?.pair.tick),
                trim: false,
              })}
            </h3>
            <Fiat
              amount={midPrice || 0n}
              as="h4"
              symbol="$"
              decimals={24}
              className="color-grey"
              padSymbol={false}
            />
          </div>
          <div className="color-grey ml-a">
            Spread{" "}
            <Decimal
              amount={spread ? (spread < 0 ? 0n : spread) : 0n}
              decimals={10}
              round={2}
              symbol="%"
            />
          </div>
        </div>
      )}
      <div className="trade__book-buys">
        {d && book
          ? [...book.bids]
              .slice(0, width > TradeBreakpoint ? 40 : 12)
              .map((e, i, all) => (
                <ItemBid
                  address={d.pair.address}
                  tick={Number(d.pair.tick)}
                  max={maxBid}
                  key={e.price}
                  k={e}
                  cumulative={all.slice(0, i + 1)}
                  symbols={[
                    d.pair.assetBase.metadata.symbol,
                    d.pair.assetQuote.metadata.symbol,
                  ]}
                />
              ))
          : null}
      </div>
    </>
  );
};

const Asks: FC<{
  d?: OrderBookFragment$data;
  k?: OrderBookSubFragment$key;
}> = ({ d, k }) => {
  const data = useFragment(subFrag, k);

  useEffect(() => {
    requestAnimationFrame(() => {
      const list = document.querySelector<HTMLElement>(".trade__book-overflow");
      if (list) {
        list.scrollTop = list.scrollHeight;
      }
    });
  }, []);

  return (
    <div className="trade__book-sells">
      {d && data
        ? [...data.asks].map((e, i, all) => (
            <ItemAsk
              address={d.pair.address}
              tick={Number(d.pair.tick)}
              max={all.reduce(
                (a, v) => (a > BigInt(v.total) ? a : BigInt(v.total)),
                0n
              )}
              key={e.price}
              k={e}
              cumulative={all.slice(0, i + 1)}
              symbols={[
                d.pair.assetBase.metadata.symbol,
                d.pair.assetQuote.metadata.symbol,
              ]}
            />
          ))
        : null}
    </div>
  );
};

const Bids: FC<{
  d?: OrderBookFragment$data;
  k?: OrderBookSubFragment$key;
}> = ({ d, k }) => {
  const data = useFragment(subFrag, k);
  useEffect(() => {
    const list = document.querySelector<HTMLElement>(".trade__book-overflow");
    if (list) list.scrollTop = 0;
  }, []);

  return (
    <div className="trade__book-buys">
      {d && data
        ? [...data.bids].map((e, i, all) => (
            <ItemBid
              address={d.pair.address}
              tick={Number(d.pair.tick)}
              max={all.reduce(
                (a, v) => (a > BigInt(v.total) ? a : BigInt(v.total)),
                0n
              )}
              key={e.price}
              k={e}
              cumulative={all.slice(0, i + 1)}
              symbols={[
                d.pair.assetBase.metadata.symbol,
                d.pair.assetQuote.metadata.symbol,
              ]}
            />
          ))
        : null}
    </div>
  );
};

const itemFragment = graphql`
  fragment OrderBookEntryFragment on FinBookEntry {
    price
    side
    total
    value
    value
    total
    virtualTotal
    virtualValue
  }
`;

interface ItemProps {
  address: string;
  k: OrderBookEntryFragment$key;
  symbols: [string, string];
  cumulative: {
    total: bigint;
    value: bigint;
    virtualTotal: bigint;
    virtualValue: bigint;
  }[];
  max: bigint;
  tick: number;
}

const ItemAsk: FC<ItemProps> = (props) => (
  <ItemContainer side="ask" {...props} />
);
const ItemBid: FC<ItemProps> = (props) => (
  <ItemContainer side="bid" {...props} />
);

const ItemContainer: FC<ItemProps & { side: "bid" | "ask" }> = (props) => {
  return (
    <Suspense fallback={<ItemContent {...props} />}>
      <Item {...props} />
    </Suspense>
  );
};

const Item: FC<ItemProps & { side: "bid" | "ask" }> = (props) => {
  const { accountData } = usePreloadedAccountData();
  const account = useFragment<OrderBookAccountFragment$key>(
    accountFragment,
    accountData
  );
  const data = useFragment(itemFragment, props.k);

  return (
    <ItemContent
      {...props}
      owned={
        !!account?.fin?.orders?.edges?.find(
          (o) =>
            o?.node?.pair?.address === props.address &&
            o?.node?.rate.toString().slice(0, 8) ===
              data.price.toString().slice(0, 8)
        )
      }
    />
  );
};

const ItemContent: FC<ItemProps & { side: "bid" | "ask"; owned?: boolean }> = ({
  k,
  cumulative,
  symbols,
  max,
  side,
  tick,
  owned,
}) => {
  const data = useFragment(itemFragment, k);
  const bar = max
    ? ((BigInt(data.total) + BigInt(data.virtualTotal)) * 100n) / max
    : 0n;
  const [, setPrice] = usePrice();
  const tipHtml = useMemo(
    () => tip(side, symbols, cumulative, tick),
    [side, symbols, cumulative]
  );

  const total =
    side === "ask"
      ? BigInt(data.total) + BigInt(data.virtualTotal)
      : BigInt(data.value) + BigInt(data.virtualValue);

  const value =
    side === "bid"
      ? BigInt(data.total) + BigInt(data.virtualTotal)
      : BigInt(data.value) + BigInt(data.virtualValue);

  return (
    <div
      className={clsx({
        "trade__book-row": true,
        "trade__book-row--sell": side === "ask",
        "trade__book-row--buy": side === "bid",
        "trade__book-row--mine": owned,
      })}
      onClick={() => {
        setPrice(adjust(BigInt(data.price), Number(tick)));
      }}
      data-tooltip-id="book-tip"
      data-tooltip-html={tipHtml}>
      <span className="trade__book-row-price">
        {priceFormatter(BigInt(data.price), { precision: tick, trim: false })}
      </span>
      <span>
        <Decimal amount={total} round={4} subscript />
      </span>
      <span>
        <Decimal amount={value} round={4} subscript />
      </span>
      <i style={{ width: bar + "%" }} />
    </div>
  );
};

const tip = (
  side: "ask" | "bid",
  symbols: [string, string],
  cumulative: {
    total: bigint;
    value: bigint;
    virtualTotal: bigint;
    virtualValue: bigint;
  }[],
  tick: number
): string => {
  const [base, quote] = cumulative.reduce(
    ([b, q], v) =>
      side === "ask"
        ? [
            b + BigInt(v.total) + BigInt(v.virtualTotal),
            q + BigInt(v.value) + BigInt(v.virtualValue),
          ]
        : [
            b + BigInt(v.value) + BigInt(v.virtualValue),
            q + BigInt(v.total) + BigInt(v.virtualTotal),
          ],
    [0n, 0n]
  );

  const avgPrice = base === 0n ? 0 : Number(quote) / Number(base);
  const avgPriceBigInt = BigInt(Math.round(avgPrice * 10 ** 12));

  return `<strong>Cumulative Depth</strong>
          <div class="mt-0.5 mono fs-13">
            <div class="flex dir-r gap-0.5">
              <p>${floatToSubscript(Number(base) / 10 ** 8, 8, 4)}</p><p class="color-lightGrey">${symbols[0]}</p>
            </div>
            <div class="flex dir-r gap-0.5">
              <p>${floatToSubscript(Number(quote) / 10 ** 8, 8, 4)}</p><p class="color-lightGrey">${symbols[1]}</p>
            </div>
            <div class="mt-1">
              <strong>Avg. Price</strong>
              <div class="flex dir-r gap-0.5 mt-0.5">
                <p>${priceFormatter(avgPriceBigInt, { precision: tick, trim: false })}</p><p class="color-lightGrey">${symbols[1]}</p>
              </div>
            </div>
          <div>`;
};

export const OrderBookFallback: FC = () => {
  return (
    <>
      <div className="trade__book-header">
        <div>{"Price"} (...)</div>
        <div className="text-right">Amount (...)</div>
        <div className="text-right">Total (...)</div>
      </div>
      <div className="trade__book-overflow">
        {[...Array(12)].map((_, i) => (
          <div className="trade__book-row" key={`skeleton-${i}`}>
            <span>
              <span className="inline-block skeleton w-12 h-1 br-2" />
            </span>
            <span>
              <span className="inline-block skeleton w-12 h-1 br-2" />
            </span>
            <span>
              <span className="inline-block skeleton w-12 h-1 br-2" />
            </span>
          </div>
        ))}
      </div>
    </>
  );
};
